import { PaginationResponseData } from './response.model';

/**
 * Subscription Filter Request DTO
 */
export class SubscriptionFilterRequestDto {
  packageName = '';
  featureName = '';
  organizationName = '';
  /** null = no status filter (return Active / Finish / Cancel together). */
  status: number | null = null;
  startDate : string | null = null;
  endDate : string | null = null;
  cancelDate: string | null = null;

  setData(data: Partial<SubscriptionFilterRequestDto>): void {
    Object.assign(this, data);
  }
}

/**
 * Subscription GetAllByFilter Request Model
 */
export class SubscriptionGetAllByFilterRequest {
  PaginationRequestModel = {
    Page: 1,
    NumberOfRecord: 50,
    SortColumn: '',
    SortOrder: '',
  };
  SubscriptionFilterRequestDto = new SubscriptionFilterRequestDto();

  setData(data: Partial<SubscriptionGetAllByFilterRequest>): void {
    if (data.PaginationRequestModel) {
      this.PaginationRequestModel = { ...this.PaginationRequestModel, ...data.PaginationRequestModel };
    }
    if (data.SubscriptionFilterRequestDto) {
      this.SubscriptionFilterRequestDto.setData(data.SubscriptionFilterRequestDto);
    }
  }
}

/** Billing period on subscription records and Subscribe requests. */
export enum SubscribePaymentType {
  Monthly = 1,
  Yearly = 2,
}

/**
 * GetAllSubscriptionByFilter `status` values.
 * 1 Active · 2 Finish · 3 Cancel
 */
export enum SubscriptionStatus {
  Active = 1,
  Finish = 2,
  Cancel = 3,
}

/**
 * Subscription DTO
 */
export class SubscriptionDto {
  id = '';
  packageId = '';
  packageName = '';
  /** Package targets Lab product surface. */
  isLab = false;
  /** Package targets Clinic product surface. */
  isClinic = false;
  /** Package targets CAD / Autobar product surface. */
  isCad = false;
  /** Whether CAD/mesh export is allowed for this subscription (from GetAllSubscriptionByFilter). */
  isExportable = false;
  /** Whether CAD share is allowed for this subscription (from GetAllSubscriptionByFilter). */
  isShareable = false;
  featureId: string | null = '';
  featureName: string | null = '';
  organizationId = '';
  organizationDescription = '';
  startDate: string | null = null;
  endDate: string | null = null;
  cancelDate: string | null = null;
  /** @see SubscriptionStatus */
  status: SubscriptionStatus | number = SubscriptionStatus.Active;
  subscriptionPaymentType: SubscribePaymentType | number | null = null;
  maxUserCount: number | null = null;
  /** Remaining seats that can be assigned (from GetAllSubscriptionByFilter). */
  availableUserCount: number | null = null;
  usedUserCount: number | null = null;
  designProjectLimit: number | null = null;
  usedDesignProject: number | null = null;
  /**
   * Next-period seat cap after a scheduled change.
   * When lower than maxUserCount → seat reduction at nextChangeDate (or endDate).
   * When equal to maxUserCount (or null) → no pending seat change.
   */
  nextMaxUserCount: number | null = null;
  /**
   * Effective date for a scheduled next* change (seat / billing / package).
   * API: NextChangeDate (forthcoming).
   */
  nextChangeDate: string | null = null;
  /**
   * Next-period billing cadence when switching monthly ↔ annual.
   * 1 = Monthly, 2 = Yearly (`SubscribePaymentType`).
   */
  nextSubscriptionPaymentType: SubscribePaymentType | number | null = null;
  /** Next-period package when a plan change is scheduled at renewal. */
  nextPackageId: string | null = null;
  nextPackageName: string | null = null;
  totalMonthlyPrice: number | null = null;
  totalYearlyPrice: number | null = null;
  totalPrice: number | null = null;
  currencyType: string | null = null;
  /** @deprecated Prefer subscriptionPaymentType (API field name). */
  subscribePaymentType: SubscribePaymentType | number | null = null;
  notificationUsers: string[] = [];
  modifiedBy = 0;
  isDeleted = false;

  setData(data: Partial<SubscriptionDto> | Record<string, unknown>): void {
    const raw = data as Record<string, unknown>;
    Object.assign(this, data);

    // API may send camelCase or PascalCase for package surface / export / share flags.
    // `null` from API is treated as false (field present but unset).
    const pickBool = (camel: keyof SubscriptionDto, pascal: string): boolean | undefined => {
      const fromCamel = (data as Partial<SubscriptionDto>)[camel];
      if (typeof fromCamel === 'boolean') {
        return fromCamel;
      }
      if (fromCamel === null) {
        return false;
      }
      const fromPascal = raw[pascal];
      if (typeof fromPascal === 'boolean') {
        return fromPascal;
      }
      if (fromPascal === null) {
        return false;
      }
      return undefined;
    };

    const isLab = pickBool('isLab', 'IsLab');
    if (isLab !== undefined) {
      this.isLab = isLab;
    }
    const isClinic = pickBool('isClinic', 'IsClinic');
    if (isClinic !== undefined) {
      this.isClinic = isClinic;
    }
    const isCad = pickBool('isCad', 'IsCad');
    if (isCad !== undefined) {
      this.isCad = isCad;
    }
    const isExportable = pickBool('isExportable', 'IsExportable');
    if (isExportable !== undefined) {
      this.isExportable = isExportable;
    }
    const isShareable = pickBool('isShareable', 'IsShareable');
    if (isShareable !== undefined) {
      this.isShareable = isShareable;
    }

    // Keep legacy alias in sync when API sends subscriptionPaymentType only.
    const paymentType =
      (data as Partial<SubscriptionDto>).subscriptionPaymentType ??
      raw['SubscriptionPaymentType'];
    if (paymentType !== undefined && (data as Partial<SubscriptionDto>).subscribePaymentType === undefined) {
      this.subscriptionPaymentType = paymentType as SubscribePaymentType | number | null;
      this.subscribePaymentType = this.subscriptionPaymentType;
    }

    const statusVal =
      (data as Partial<SubscriptionDto>).status ?? raw['Status'];
    if (statusVal !== undefined && statusVal !== null) {
      this.status = Number(statusVal);
    }

    const nextMax =
      (data as Partial<SubscriptionDto>).nextMaxUserCount ?? raw['NextMaxUserCount'];
    if (nextMax !== undefined) {
      this.nextMaxUserCount =
        nextMax === null || nextMax === '' ? null : Number(nextMax);
    }

    const nextChange =
      (data as Partial<SubscriptionDto>).nextChangeDate ?? raw['NextChangeDate'];
    if (nextChange !== undefined) {
      this.nextChangeDate =
        nextChange === null || nextChange === '' ? null : String(nextChange);
    }

    const nextPay =
      (data as Partial<SubscriptionDto>).nextSubscriptionPaymentType ??
      raw['NextSubscriptionPaymentType'];
    if (nextPay !== undefined) {
      this.nextSubscriptionPaymentType =
        nextPay === null || nextPay === ''
          ? null
          : (Number(nextPay) as SubscribePaymentType);
    }

    const nextPkgId =
      (data as Partial<SubscriptionDto>).nextPackageId ?? raw['NextPackageId'];
    if (nextPkgId !== undefined) {
      this.nextPackageId =
        nextPkgId === null || nextPkgId === '' ? null : String(nextPkgId);
    }

    const nextPkgName =
      (data as Partial<SubscriptionDto>).nextPackageName ?? raw['NextPackageName'];
    if (nextPkgName !== undefined) {
      this.nextPackageName =
        nextPkgName === null || nextPkgName === '' ? null : String(nextPkgName);
    }
  }
}

/**
 * Subscription Response Model
 */
export type SubscriptionResponseModel = PaginationResponseData<SubscriptionDto>;
